import { fetchEndpointData } from '@utils/fetchEndpointData';

import type { CsrfTokenResponse } from './endpoints/CsrfTokenEndpoint.types';
import type { MeResponse } from './endpoints/MeEndpoint.types';
import type { WebPasswordLoginResponse } from './endpoints/WebPasswordLoginEndpoint.types';
import type { WebForgotPasswordResponse } from './endpoints/WebForgotPasswordEndpoint.types';
import type { WebSetPasswordResponse } from './endpoints/WebSetPasswordEndpoint.types';
import type { ChangePasswordResponse } from './endpoints/ChangePasswordEndpoint.types';

export type { MeResponse } from './endpoints/MeEndpoint.types';

export async function getMe(): Promise<MeResponse> {
  return fetchEndpointData<MeResponse>('/api/v1/auth/me');
}

export async function getCsrfToken(): Promise<CsrfTokenResponse> {
  return fetchEndpointData<CsrfTokenResponse>('/api/v1/auth/csrf-token');
}

export async function logout(): Promise<void> {
  const { token } = await getCsrfToken();
  // eslint-disable-next-line no-restricted-globals
  window.location.href = `/api/v1/auth/logout?token=${encodeURIComponent(token)}`;
}

/**
 * REST equivalent of the GraphQL `passwordLogin` mutation. Returns
 * `{ success }`; throws (via fetchEndpointData) only on transport / rate-limit
 * errors, mirroring the mutation returning `false` for bad credentials.
 */
export async function webPasswordLogin(email: string, password: string): Promise<WebPasswordLoginResponse> {
  return fetchEndpointData<WebPasswordLoginResponse>('/api/v1/auth/web/login', {
    method: 'POST',
    body: { email, password },
  });
}

/**
 * REST equivalent of the GraphQL `forgotPassword` mutation. Always resolves
 * `{ success: true }` for any registered/unregistered email; throws only on
 * transport / rate-limit errors.
 */
export async function webForgotPassword(email: string, lang?: string): Promise<WebForgotPasswordResponse> {
  return fetchEndpointData<WebForgotPasswordResponse>('/api/v1/auth/web/forgot-password', {
    method: 'POST',
    body: { email, lang },
  });
}

/**
 * REST equivalent of the GraphQL `setPassword` mutation. Returns `{ success }`;
 * `success: false` means an invalid/expired token, while a thrown error means
 * the new password failed validation (e.g. too short).
 */
export async function webSetPassword(token: string, newPassword: string): Promise<WebSetPasswordResponse> {
  return fetchEndpointData<WebSetPasswordResponse>('/api/v1/auth/web/set-password', {
    method: 'POST',
    body: { token, newPassword },
  });
}

/**
 * Authenticated self-service password change for external users. Returns
 * `{ success: true }`; throws (via fetchEndpointData) on a wrong current
 * password, a too-short new password, or for internal users.
 */
export async function changePassword(oldPassword: string, newPassword: string): Promise<ChangePasswordResponse> {
  return fetchEndpointData<ChangePasswordResponse>('/api/v1/auth/web/change-password', {
    method: 'POST',
    body: { oldPassword, newPassword },
  });
}
